import { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { BulkRemovePredeliveryDialog } from 'src/dialogs/BulkEdits/BulkRemovePredeliveryDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
  Permission,
} from 'src/WebApiController';

import { INVENTORY_BULK_REMOVE_PREDELIVERY_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchRemovePredelivery } from './LaunchRemovePredelivery';

export const useBulkRemovePredelivery = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_Predelivery,
    Permission.Inventory_PredeliveryOnIsPricerOf,
    Permission.Inventory_PredeliveryOnPurchasedBy
  );
  const { activeAccountWebClientConfig } = useAppContext();
  const { setSelectionMode } = useMultiSelectionContext();

  const updateKey = event
    ? `inventory-predelivery-event-${event.virtId}`
    : INVENTORY_BULK_REMOVE_PREDELIVERY_UPDATE_KEY;

  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkRemovePredeliveryDialog,
    closeDialog: closeBulkRemovePredeliveryDialog,
  } = useDialog(DialogId.BulkRemovePredilivery, BulkRemovePredeliveryDialog);

  const onSubmit = useCallback(
    async (
      removeBarcodes: boolean,
      removePdfs: boolean,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkRemovePredeliveryPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkRemovePredeliveryPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkRemovePredelivery(
              preview!.preview,
              removeBarcodes,
              removePdfs,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                setSelectionMode();
                await refreshExpandedListItems();
              }

              closeBulkRemovePredeliveryDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkRemovePredelivery', error, {
              trackErrorData: {
                preview,
                removeBarcodes,
                removePdfs,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkRemovePredeliveryDialog,
      filterQueryWithEventIds,
      refreshExpandedListItems,
      setIsLoading,
      setSelectionMode,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkRemovePredeliveryDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkRemovePredeliveryDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditRemovePredelivery,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditRemovePredelivery,
        disabled: !hasPermission,
      });
    };
  }, [hasPermission, openDialogStableRef, registerCommand, unregisterCommand]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkRemovePredeliveryDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkRemovePredeliveryDialog,
      });
    },
    [
      closeBulkRemovePredeliveryDialog,
      onSubmit,
      openBulkRemovePredeliveryDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasPermission) {
      return [];
    }
    return [
      {
        title: ContentId.RemovePredelivery,
        disabled: isLoading,
        onClick,
      },
    ];
  }, [hasPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchRemovePredelivery
        key="removePredeliveredArtifacts"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
